import { DotsThreeIcon } from "@phosphor-icons/react";
import {
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
	type ComponentType,
	type KeyboardEvent as ReactKeyboardEvent,
	type MouseEvent as ReactMouseEvent,
	type ReactNode,
} from "react";

import { cn } from "../cn.js";
import { AnchoredPanel } from "./AnchoredPanel.js";

export type SidebarAccountWorkspace = {
	id: string;
	label: string;
	active: boolean;
};

export type SidebarAccountScrollContainerProps = {
	children: ReactNode;
	className?: string;
	role?: string;
	"aria-label"?: string;
};

export interface SidebarAccountProps {
	name: string;
	email: string;
	settingsHref: string;
	/** Opens account settings outside the webmail scope without leaving the installed PWA shell. */
	settingsExternal?: boolean;
	onSettingsClick?: () => void;
	onSignOut: () => void;
	signingOut?: boolean;
	darkMode?: boolean;
	onDarkModeChange?: (dark: boolean) => void;
	workspaces?: SidebarAccountWorkspace[];
	onSwitchWorkspace?: (organisationId: string) => void;
	/** Override the workspace list scroller (Key Platform uses shadcn ScrollArea). */
	workspaceScrollContainer?: ComponentType<SidebarAccountScrollContainerProps>;
}

export function accountInitials(name: string, email: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	const first = parts[0]?.[0];
	const second = parts[1]?.[0];
	if (first && second) return `${first}${second}`.toUpperCase();
	if (first) return first.toUpperCase();
	const fromEmail = email.trim()[0];
	return fromEmail ? fromEmail.toUpperCase() : "?";
}

export function accountDisplayName(name: string, email: string): string {
	const trimmed = name.trim();
	if (trimmed) return trimmed;
	const local = email.split("@")[0]?.trim();
	return local || "Account";
}

export function SidebarAccount({
	name,
	email,
	settingsHref,
	settingsExternal = false,
	onSettingsClick,
	onSignOut,
	signingOut = false,
	darkMode,
	onDarkModeChange,
	workspaces,
	onSwitchWorkspace,
	workspaceScrollContainer: WorkspaceScrollContainer,
}: SidebarAccountProps) {
	const [open, setOpen] = useState(false);
	const showWorkspaceList =
		workspaces !== undefined && workspaces.length > 0 && onSwitchWorkspace !== undefined;
	const WorkspaceScroller = WorkspaceScrollContainer ?? "div";
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const menuId = useId();
	const label = accountDisplayName(name, email);
	const initials = accountInitials(name, email);

	const close = useCallback((restoreFocus = true) => {
		setOpen(false);
		if (restoreFocus) triggerRef.current?.focus();
	}, []);

	useEffect(() => {
		if (!open) return;
		const first = panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
		first?.focus();
	}, [open]);

	const moveFocus = (direction: 1 | -1 | "first" | "last") => {
		const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
		if (items.length === 0) return;
		const active = document.activeElement as HTMLElement | null;
		const index = active ? items.indexOf(active) : -1;
		let next = 0;
		if (direction === "first") next = 0;
		else if (direction === "last") next = items.length - 1;
		else next = (index + direction + items.length) % items.length;
		items[next]?.focus();
	};

	const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
		if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			setOpen(true);
		}
		if (event.key === "Escape" && open) {
			event.preventDefault();
			close();
		}
	};

	const onSettings = (event: ReactMouseEvent<HTMLAnchorElement>) => {
		if (
			onSettingsClick &&
			event.button === 0 &&
			!event.metaKey &&
			!event.ctrlKey &&
			!event.shiftKey &&
			!event.altKey
		) {
			event.preventDefault();
			onSettingsClick();
		}
		close(false);
	};

	return (
		<div className="flex min-w-0 items-center gap-2">
			<span
				aria-hidden="true"
				className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-(--color-brand-50) text-xs font-semibold text-(--color-brand-700)"
			>
				{initials}
			</span>
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-(--text-primary)">{label}</p>
				<p className="truncate text-xs text-(--text-muted)">{email}</p>
			</div>
			<button
				ref={triggerRef}
				type="button"
				aria-label={`Account menu for ${label}`}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={menuId}
				onClick={() => setOpen((current) => !current)}
				onKeyDown={onTriggerKeyDown}
				className={cn(
					"inline-flex size-8 shrink-0 items-center justify-center rounded-(--radius-md) border-0 bg-transparent",
					"text-(--text-muted) hover:bg-(--surface-sunken) hover:text-(--text-primary)",
					open && "bg-(--surface-sunken) text-(--text-primary)",
				)}
			>
				<DotsThreeIcon className="size-5" aria-hidden="true" />
			</button>
			<AnchoredPanel
				ref={panelRef}
				open={open}
				anchorRef={triggerRef}
				align="end"
				onDismiss={() => setOpen(false)}
				id={menuId}
				role="menu"
				aria-label={`Account menu for ${label}`}
				tabIndex={-1}
				onKeyDown={(event) => {
					switch (event.key) {
						case "Escape":
							event.preventDefault();
							close();
							break;
						case "ArrowDown":
							event.preventDefault();
							moveFocus(1);
							break;
						case "ArrowUp":
							event.preventDefault();
							moveFocus(-1);
							break;
						case "Home":
							event.preventDefault();
							moveFocus("first");
							break;
						case "End":
							event.preventDefault();
							moveFocus("last");
							break;
						case "Tab":
							close(false);
							break;
						default:
							break;
					}
				}}
				className={cn("min-w-44", showWorkspaceList && "min-w-52")}
			>
				{showWorkspaceList && (
					<WorkspaceScroller
						role="group"
						aria-label="Workspaces"
						className={
							WorkspaceScrollContainer
								? undefined
								: "max-h-[min(240px,40vh)] overflow-y-auto border-b border-(--border-subtle) pb-0.5"
						}
					>
						{workspaces.map((workspace) => (
							<button
								key={workspace.id}
								type="button"
								role="menuitem"
								tabIndex={-1}
								aria-current={workspace.active ? "true" : undefined}
								onClick={() => {
									if (!workspace.active) onSwitchWorkspace(workspace.id);
									close(false);
								}}
								className={cn(
									"flex w-full rounded-(--radius-sm) px-2.5 py-2 text-left text-sm outline-none hover:bg-(--surface-sunken) focus-visible:bg-(--surface-sunken)",
									workspace.active ? "font-medium text-(--text-primary)" : "text-(--text-primary)",
								)}
							>
								{workspace.label}
							</button>
						))}
					</WorkspaceScroller>
				)}
				<a
					href={settingsHref}
					target={settingsExternal ? "_blank" : undefined}
					rel={settingsExternal ? "noopener noreferrer" : undefined}
					role="menuitem"
					tabIndex={-1}
					onClick={onSettings}
					className="block rounded-(--radius-sm) px-2.5 py-2 text-sm text-(--text-primary) outline-none hover:bg-(--surface-sunken) focus-visible:bg-(--surface-sunken)"
				>
					Settings
				</a>
				{onDarkModeChange && (
					<button
						type="button"
						role="menuitem"
						tabIndex={-1}
						aria-checked={darkMode === true}
						onClick={() => onDarkModeChange(!darkMode)}
						className="flex w-full items-center justify-between gap-3 rounded-(--radius-sm) px-2.5 py-2 text-left text-sm text-(--text-primary) outline-none hover:bg-(--surface-sunken) focus-visible:bg-(--surface-sunken)"
					>
						Dark mode
						<span
							aria-hidden="true"
							className={cn(
								"relative h-[18px] w-8 shrink-0 rounded-full transition-colors",
								darkMode ? "bg-(--color-brand-600)" : "bg-(--color-ink-200)",
							)}
						>
							<span
								className={cn(
									"absolute top-[2px] left-[2px] size-3.5 rounded-full bg-white shadow-sm transition-transform",
									darkMode && "translate-x-3.5",
								)}
							/>
						</span>
					</button>
				)}
				<button
					type="button"
					role="menuitem"
					tabIndex={-1}
					disabled={signingOut}
					onClick={() => {
						close(false);
						onSignOut();
					}}
					className="flex w-full rounded-(--radius-sm) px-2.5 py-2 text-left text-sm text-(--color-critical-700) outline-none hover:bg-(--surface-sunken) focus-visible:bg-(--surface-sunken) disabled:cursor-not-allowed disabled:opacity-60"
				>
					{signingOut ? "Signing out…" : "Sign out"}
				</button>
			</AnchoredPanel>
		</div>
	);
}
